import template from './menu.html'
import { emitTrack, WidgetSettings } from '@/tracker'

import ContrastButtons from './ContrastButtons'
import ReadAssistanceButtons from './ReadAssistanceButtons'
import VisualAssistanceButtons from './VisualAssistanceButtons'

import renderButtons from './renderButtons'
import adjustFontSize from '../../tools/adjustFontSize'
import renderTools from './renderTools'
import reset from './reset'

import css from './menu.css'
import enableContrast from '@/tools/enableContrast'
import { pluginConfig } from '@/globals/pluginConfig'
import { userSettings, saveUserSettings, getActiveStatesCount } from '@/globals/userSettings'
import toggleMenu from './toggleMenu'
import { $widget } from '../widget/widget'
import { t } from '@/i18n/translate'

let menuContainerInternal: HTMLElement | null = null

export const getMenuContainer = (): HTMLElement | null => menuContainerInternal

function buildSettings(): WidgetSettings {
  const s = userSettings.states
  return {
    font_size: s.fontSize ?? 1,
    contrast: s.contrast ?? false,
    large_cursor: !!s['big-cursor'],
    reading_guide: !!s['readable-guide'],
    screen_reader: !!s['screen-reader'],
    dyslexia_font: !!s['readable-font'],
  }
}

export function updateActiveIndicators(menu: HTMLElement) {
  const count = getActiveStatesCount()
  const isActive = count > 0

  menu.querySelector('.asw-status-pill')?.classList.toggle('asw-active', isActive)

  const $resetBtn = menu.querySelector('.asw-menu-reset') as HTMLButtonElement | null
  if ($resetBtn) $resetBtn.disabled = !isActive

  const $btn = $widget?.querySelector('.asw-menu-btn')
  if ($btn) $btn.classList.toggle('asw-active', isActive)

  const $btnTitle = $widget?.querySelector('.asw-title')
  if ($btnTitle) $btnTitle.classList.toggle('asw-active', isActive)

  // Update translated label
  const pillEl = menu.querySelector('.asw-status-pill') as HTMLElement | null
  if (pillEl) {
    pillEl.textContent = t(isActive ? 'Active' : 'Inactive')
  }
}

export default function renderMenu() {
  const $container: HTMLElement = document.createElement('div')
  $container.innerHTML = `<style>${css}</style>${template}`

  menuContainerInternal = $container.querySelector('.asw-menu') as HTMLElement
  const $menu = menuContainerInternal

  if (pluginConfig?.position?.includes('right')) {
    $menu.style.right = '0px'
    $menu.style.left = 'auto'
  }

  $menu.querySelector('.visual-assistance').innerHTML = renderButtons(
    VisualAssistanceButtons,
    'asw-visual-assistence',
  )
  $menu.querySelector('.contrast').innerHTML = renderButtons(ContrastButtons, 'asw-contrast')
  $menu.querySelector('.read-assistance').innerHTML = renderButtons(
    ReadAssistanceButtons,
    'asw-read-assistance',
  )

  // *** States UI Rendering ***
  const states = userSettings?.states

  const fontSize = Number(states?.fontSize) || 1
  $menu.querySelector('.asw-amount').innerHTML = `${(fontSize * 100).toFixed(0)}%`

  if (states) {
    const buttons = Array.from($menu.querySelectorAll('.asw-btn'))

    Object.entries(states).forEach(([key, value]) => {
      if (value && key !== 'fontSize') {
        const selector = key === 'contrast' ? states[key] : key
        const btn = buttons.find((b) => (b as HTMLElement).dataset.key === selector)
        if (btn) btn.classList.add('asw-selected')
      }
    })
  }

  // *** Translations: language is hardcoded in pluginConfig.lang (id) ***
  // *** Utils ***
  $container.querySelectorAll('.asw-menu-close').forEach((el) => el.addEventListener('click', toggleMenu))
  $container.querySelectorAll('.asw-menu-reset').forEach((el) => el.addEventListener('click', reset))

  // click outside dialog closes the modal
  menuContainerInternal.addEventListener('click', (event) => {
    if (event.target === menuContainerInternal) {
      emitTrack('CLOSE_ACCESSIBILITY_WIDGET', buildSettings())
      menuContainerInternal.remove()
    }
  })

  // *** Controls ***
  $menu.querySelectorAll('.asw-plus, .asw-minus').forEach((el: HTMLElement) => {
    el.addEventListener('click', (event) => {
      event.stopPropagation()
      event.stopImmediatePropagation()
      const difference = 0.1

      let fontSize = userSettings?.states?.fontSize || 1
      if (el.classList.contains('asw-minus')) {
        fontSize -= difference
      } else {
        fontSize += difference
      }

      fontSize = Math.max(fontSize, 0.1)
      fontSize = Math.min(fontSize, 2)
      fontSize = Number(fontSize.toFixed(2))

      document.querySelector('.asw-amount').textContent = `${(fontSize * 100).toFixed(0)}%`

      const pressed = fontSize !== 1
      el.setAttribute('aria-pressed', String(pressed))

      adjustFontSize(fontSize)
      userSettings.states.fontSize = fontSize

      saveUserSettings()
      updateActiveIndicators($menu)
    })
  })

  $menu.querySelectorAll('.asw-btn').forEach((el: HTMLElement) => {
    el.addEventListener('click', () => {
      const key = el.dataset.key
      const isSelected = !el.classList.contains('asw-selected')

      // --- Contrast ---
      if (el.classList.contains('asw-contrast')) {
        $menu
          .querySelectorAll('.asw-contrast')
          .forEach((el: HTMLElement) => el.classList.remove('asw-selected'))

        if (isSelected) {
          el.classList.add('asw-selected')
        }

        userSettings.states.contrast = isSelected ? key : false
        enableContrast(userSettings.states.contrast)

        saveUserSettings()
        updateActiveIndicators($menu)

        return
      }

      el.classList.toggle('asw-selected', isSelected)
      userSettings.states[key] = isSelected
      renderTools()

      saveUserSettings()
      updateActiveIndicators($menu)
    })
  })

  updateActiveIndicators($menu)
  $widget.appendChild($container)

  return $container
}
